import React, { useEffect, useMemo, useRef, useState } from 'react';
import './SearchableSelect.css';

// ── Searchable dropdown (TSB-4302) ────────────────────────────────────────────
//
// Ported from the project selector in the ecoh-ui-module repo
// (src/features/dashboard/components/toolbar/DashboardToolbar.jsx) — the pattern
// the ticket asks us to match: a button trigger, a panel with a pinned search
// box, and a case-insensitive substring filter over the options.
//
// Three deliberate differences from that original, so a future diff against it
// reads as "the same, except this":
//
// 1. No portal. ecoh-ui portals its panel to document.body, which is safe there
//    because its theme tokens sit on :root. Ours are scoped to .v2-shell, so a
//    portalled panel resolves every var() to nothing and ignores light mode.
//    The panel is absolutely positioned inside the filter bar instead — nothing
//    between here and .v2-main clips overflow. That also removes ecoh-ui's rect
//    measurement and its close-on-scroll/resize listeners, which only existed to
//    keep a fixed-position panel glued to its trigger.
//
// 2. Escape closes and Enter picks the first match. The native <select> this
//    replaces gave us keyboard control for free, so going mouse-only would have
//    been a regression. Arrow-key navigation and full combobox ARIA are out of
//    scope — TSB-4302 is about search.
//
// 3. An explicit "No matches" row, so a query that hits nothing reads as empty
//    rather than broken.
//
// `options` is [{ id, label }]. The empty string is the "no filter" value, which
// matches what the <select> this replaced used.
//
// `triggerClassName` lets each page style the closed control like the ones next
// to it — .v2-filter-select on the Analytics filter bar, .txd-dropdown-btn on
// the Dashboard topbar. Only the trigger differs; the panel is shared.
function SearchableSelect({ value, options, onChange, allLabel, placeholder, triggerClassName = 'v2-filter-select' }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef   = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = () => {
    if (!open) {
      setSearch('');
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    setOpen(o => !o);
  };

  const pick = id => {
    onChange(id);
    setOpen(false);
    setSearch('');
  };

  const handleKeyDown = e => {
    if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    } else if (e.key === 'Enter' && filtered.length > 0) {
      e.preventDefault();
      pick(filtered[0].id);
    }
  };

  const selected = options.find(o => o.id === value);

  return (
    <div className="v2-ss" ref={wrapRef}>
      <button
        type="button"
        className={`${triggerClassName} v2-ss-trigger${open ? ' v2-ss-trigger--open' : ''}`}
        onClick={toggle}
      >
        <span className="v2-ss-value">{selected ? selected.label : allLabel}</span>
      </button>

      {open && (
        <div className="v2-ss-panel">
          <div className="v2-ss-search-wrap">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchRef}
              className="v2-ss-search"
              type="text"
              placeholder={placeholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              onClick={e => e.stopPropagation()}
            />
            {search && (
              <button
                type="button"
                className="v2-ss-search-clear"
                aria-label="Clear search"
                onClick={() => { setSearch(''); searchRef.current?.focus(); }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className="v2-ss-options">
            {/* the reset row only makes sense with an empty box — while searching
                it is one more row that does not match what you typed */}
            {!search && (
              <div
                className={`v2-ss-option${!value ? ' v2-ss-option--selected' : ''}`}
                onClick={() => pick('')}
              >
                {allLabel}
              </div>
            )}
            {filtered.map(o => (
              <div
                key={o.id}
                className={`v2-ss-option${value === o.id ? ' v2-ss-option--selected' : ''}`}
                onClick={() => pick(o.id)}
                title={o.label}
              >
                {o.label}
              </div>
            ))}
            {filtered.length === 0 && <div className="v2-ss-empty">No matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
